import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { voiceCloneService, VoiceClone } from "../services/voiceCloneService";

const VOICE_CLONE_KEY = "voiceClones";

/**
 * Fetch all voice clones for the current user.
 * Uses shorter staleTime when any clone is still processing.
 */
export function useVoiceClones() {
  return useQuery<VoiceClone[]>({
    queryKey: [VOICE_CLONE_KEY],
    queryFn: () => voiceCloneService.getVoiceClones(),
    staleTime: 5 * 60 * 1000,
    // Poll every 10s when there's a processing clone
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasProcessing = data?.some((c) => c.status === "processing");
      return hasProcessing ? 10_000 : false;
    },
  });
}

/**
 * Upload a voice sample and create a voice clone.
 */
export function useCreateVoiceClone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, name, description, consentAcknowledged }: { file: File; name: string; description?: string; consentAcknowledged?: boolean }) =>
      voiceCloneService.uploadVoiceSample(file, name, description, consentAcknowledged),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VOICE_CLONE_KEY] });
    },
  });
}

/**
 * Delete a voice clone.
 */
export function useDeleteVoiceClone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => voiceCloneService.deleteVoiceClone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VOICE_CLONE_KEY] });
    },
  });
}

/**
 * Set a voice clone as the default.
 */
export function useSetDefaultVoiceClone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => voiceCloneService.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VOICE_CLONE_KEY] });
    },
  });
}

/**
 * Generate a preview audio clip with the cloned voice.
 */
export function usePreviewVoiceClone() {
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      voiceCloneService.preview(id, text),
  });
}

/**
 * Test RVM: drop a voicemail to the agent's own phone.
 */
export function useTestRVM() {
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text?: string }) =>
      voiceCloneService.testRvm(id, text),
  });
}
